import videojs from 'video.js'
import 'videojs-contrib-hls'
import isSafari from '../utils/isSafari';
import isIos from '../utils/isIos';
import EventBus from '@/utils/EventBus'

let nextId = 0
export class ClipBuffer {
    constructor(clip, containerElement) {
        this.id = nextId++
        this.events = new EventBus()

        this.clip = clip
        this.startSeconds = (clip.start_time || 0) / 1000
        this.endSeconds = (clip.end_time || clip.video.durationInMilliseconds) / 1000

        this.hasLoadedMetadata = false

        // Is the video currently scrubbed to a time that's within bounds for the clip?
        this.isInBounds = false

        // Is the video buffered enough to play?
        this.canPlay = false

        // Should the video be playing if able to?
        this.playRequested = false

        // The last time that we programmatically attempted to scrub to
        this.lastScrubbedTime = this.startSeconds

        const sourceElement = this.createSourceElement()
        containerElement.appendChild(sourceElement)

        this.player = videojs(sourceElement, {
            controls: false,
            muted: isSafari(), // Set muted by default in Safari to allow autoplay
        })

        this.player.ready(() => this.onReady())

        this._checkTime()
        this.setVolume(0.5)
        this.pause()
    }

    // Safari on iOS sometimes doesn't ever seem to want to set currentTime properly.
    // This seems to fix it for some eldritch reason.
    doIosHack() {
        if (isIos()) {
            this.player.play().then(() => this.player.pause()).catch(() => {})
        }
    }

    onReady() {
        this.doIosHack()
        this._checkTime()
        this.player.on('loadedmetadata', () => this.onLoadedMetadata())
        this.player.on('timeupdate', () => this._checkTime())
        this.player.on('canplay', () => this.onCanPlay())
        this.player.on('loadeddata', () => this.onLoadedData())
        this.player.on('waiting', () => this.onWaiting())
    }

    onLoadedMetadata() {
        this.hasLoadedMetadata = true

        this._checkTime()

        this.updateInterval = setInterval(() => this._checkTime(), 10000)
    }

    onLoadedData() {
        //
    }

    // Fired when the player has buffered enough to play
    onCanPlay() {
        this.canPlay = true
        this._checkTime()

        // If we're supposed to be playing (but couldn't before), play
        if (this.playRequested) {
            this.player.play()
        }
    }

    // Fired when the player is waiting to buffer.
    // We pause here because for some reason, some browsers will continue playing
    // the video even while "waiting", but without firing timeupdate events,
    // so that we don't know when to end the clip.
    onWaiting() {
        this.canPlay = false
        this.player.pause()
        this._checkTime()
    }

    _setCurrentTime(time) {
        // Prevent weird Chrome stuttering bug
        this.player.currentTime(Math.max(0.1, time))
    }

    getCurrentTime() {
        return this.player.currentTime()
    }

    _checkTime() {
        const currentTime = this.player.currentTime()

        // We fuzz this a little bit (+/- 1 second) because of imprecision in some browsers.
        if (currentTime < this.lastScrubbedTime - 1 || currentTime > this.endSeconds + 1) {
            this.isInBounds = false

            this.doIosHack()

            // Try to get in bounds
            this._setCurrentTime(this.lastScrubbedTime)
        } else {
            this.isInBounds = true
        }

        if (this.isInBounds) {
            this.events.fire('timeUpdate', currentTime)
        }

        if (this.player.currentTime() >= this.endSeconds) {
            const wasPaused = !this.playRequested

            this.pause()

            if (!wasPaused) {
                this._setCurrentTime(this.startSeconds)
                this.events.fire('finish')
            }
        }
    }

    createSourceElement() {
        const video = this.clip.video
        const sourceUrl = this.clip.video.video_host === 'connectsports' ?  this.clip.video.playable_video_host_id :
            `https://video-content.sportsrecruits.com/${video.video_host_id}/hls/index.m3u8`;
        const type = this.clip.video.video_host === 'connectsports' ? 'video/mp4' : 'application/x-mpegURL';
        
        return $(`
            <video class="video-js vjs-default-skin" playsinline>
                <source src="${sourceUrl}" type="${type}">
            </video>
        `).get(0)
    }

    start() {
        this._setCurrentTime(this.startSeconds)
        this.resume()
    }

    destroy() {
        this.player.dispose()

        if (this.updateInterval) {
            clearInterval(this.updateInterval)
        }
    }

    pause() {
        this.playRequested = false
        this.player.pause()
    }

    resume() {
        this.playRequested = true
        
        setTimeout(() => {
            if (this.canPlay && this.playRequested) {
                this.player.play()
            }
        })
    }

    setMuted(muted) {
        this.player.muted(muted)
    }

    setVolume(volume) {
        this.player.volume(volume)
    }

    scrubToRelativeFraction(fraction) {
        this.scrubToRelativeTime(fraction * (this.endSeconds - this.startSeconds))
    }

    scrubToRelativeTime(relativeTime) {
        this.scrubToAbsoluteTime(this.startSeconds + relativeTime)
    }

    scrubToAbsoluteTime(time) {
        time = Math.max(this.startSeconds, Math.min(this.endSeconds, time))

        this.lastScrubbedTime = time

        if (this.hasLoadedMetadata) {
            this._setCurrentTime(time)
        }

        this.events.fire('timeUpdate', time)
    }

    on(...args) {
        return this.events.on(...args)
    }

    off(...args) {
        return this.events.off(...args)
    }
}
